import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { 
    ChangeDetectionStrategy, 
    ChangeDetectorRef, 
    Component, 
    OnDestroy, 
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation 
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { MailboxService } from 'app/modules/admin/mailbox/mailbox.service';
import { environment } from 'environments/environment';
import { Subject, takeUntil, catchError, of } from 'rxjs';

@Component({
    selector: 'messages',
    standalone: true,
    templateUrl: './messages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatButtonModule, 
        MatIconModule, 
        MatTooltipModule,
        DatePipe
    ]
})
export class MessagesComponent implements OnInit, OnDestroy {
    @ViewChild('messagesOrigin') private _messagesOrigin: MatButton;
    @ViewChild('messagesPanel') private _messagesPanel: TemplateRef<any>;
    
    unreadCount: number = 0;
    unreadEmails: any[] = [];
    isLoading: boolean = false;
    
    // Cache management
    private _lastFetchedCount: number = -1;
    private _lastFetchTime: number = 0;
    private _cacheValidityMs: number = 30000; // 30 seconds cache
    
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private apiUrl = environment.nylasUrl;

    constructor(
        private _router: Router,
        private _mailboxService: MailboxService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _httpClient: HttpClient
    ) {}

    ngOnInit(): void {
        console.log('MessagesComponent Initializing...');
        
        // Subscribe to unread count changes
        this._mailboxService.unreadCount$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((count: number) => {
                console.log('MessagesComponent Unread count received:', count);
                const countChanged = this.unreadCount !== count;
                this.unreadCount = count;
                
                // If count changed and we have emails to show, refresh in background
                if (countChanged && count > 0) {
                    this._lastFetchedCount = -1;
                    // Pre-fetch emails in background so popup is instant
                    this.fetchUnreadEmails();
                }
                
                this._changeDetectorRef.markForCheck();
            });
        
        // Start polling for unread count updates — only when Nylas is linked.
        if (this._mailboxService.hasNylasGrant()) {
            console.log('MessagesComponent Starting polling...');
            this._mailboxService.startUnreadCountPolling();

            // Pre-fetch unread emails immediately so popup is ready
            // Small delay to let the unread count load first
            setTimeout(() => {
                if (this.unreadEmails.length === 0) {
                    this.fetchUnreadEmails();
                }
            }, 500);
        } else {
            console.log('MessagesComponent Skipping email polling — no Nylas grant');
            this.unreadCount = 0;
            this._changeDetectorRef.markForCheck();
        }
    }

    ngOnDestroy(): void {
        this._mailboxService.stopUnreadCountPolling();
        
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    /**
     * Open the messages panel and fetch unread emails if needed
     */
    openPanel(): void {
        if (!this._messagesPanel || !this._messagesOrigin) {
            return;
        }

        if (!this._overlayRef) {
            this._createOverlay();
        }

        if (this._overlayRef.hasAttached()) {
            return;
        }

        this._overlayRef.attach(
            new TemplatePortal(this._messagesPanel, this._viewContainerRef)
        );
        
        // Only fetch if cache is stale or count changed (show loading since panel is open)
        if (this._shouldRefetch()) {
            this.fetchUnreadEmails(true);
        }
    }

    /**
     * Check if we should refetch emails
     */
    private _shouldRefetch(): boolean {
        const now = Date.now();
        const cacheExpired = (now - this._lastFetchTime) > this._cacheValidityMs;
        const countChanged = this._lastFetchedCount !== this.unreadCount;
        const noData = this.unreadEmails.length === 0 && this.unreadCount > 0;
        
        if (countChanged) {
            console.log('MessagesComponent: Count changed, refetching...');
            return true;
        }
        if (cacheExpired) {
            console.log('MessagesComponent: Cache expired, refetching...');
            return true;
        }
        if (noData) {
            console.log('MessagesComponent: No cached data, fetching...');
            return true;
        }
        
        console.log('MessagesComponent: Using cached emails');
        return false;
    }

    /**
     * Fetch unread emails from Nylas API
     * @param showLoading Whether to show loading indicator (false for background pre-fetch)
     */
    private fetchUnreadEmails(showLoading: boolean = false): void {
        if (showLoading) {
            this.isLoading = true;
            this._changeDetectorRef.markForCheck();
        }
        
        const url = `${this.apiUrl}/threads?in=INBOX&limit=20`;
        
        this._httpClient.get<any>(url).pipe(
            catchError((err) => {
                console.error('MessagesComponent Error fetching emails:', err);
                return of({ data: [] });
            })
        ).subscribe(response => {
            const threads = response?.data || [];
            
            // Filter to only unread threads and format them
            this.unreadEmails = threads
                .filter((thread: any) => thread.unread === true)
                .slice(0, 10)
                .map((thread: any) => this.formatThread(thread));
            
            // Update cache tracking
            this._lastFetchedCount = this.unreadCount;
            this._lastFetchTime = Date.now();
            
            console.log('MessagesComponent: Cached', this.unreadEmails.length, 'unread emails');
            this.isLoading = false;
            this._changeDetectorRef.markForCheck();
        });
    }

    /**
     * Format a Nylas thread into a display-friendly object
     */
    private formatThread(thread: any): any {
        const participants = thread.participants || [];
        const from = participants.find((p: any) => p.email !== this.getCurrentUserEmail()) || participants[0] || {};
        
        return {
            id: thread.id,
            thread_id: thread.id,
            subject: thread.subject || '(No subject)',
            from: {
                name: from.name || from.email || 'Unknown',
                email: from.email || ''
            },
            content: thread.snippet || '',
            date: thread.last_message_timestamp ? new Date(thread.last_message_timestamp * 1000).toISOString() : new Date().toISOString(),
            unread: thread.unread,
            folder: 'inbox'
        };
    }

    /**
     * Get current user email (for filtering out self from participants)
     */
    private getCurrentUserEmail(): string {
        try {
            const userData = localStorage.getItem('userData');
            if (userData) {
                const user = JSON.parse(userData);
                return user.email || '';
            }
        } catch (e) {}
        return '';
    }

    /**
     * Close the messages panel
     */
    closePanel(): void {
        if (this._overlayRef) {
            this._overlayRef.detach();
        }
    }

    /**
     * Navigate to a specific email in the mailbox
     */
    openEmail(mail: any): void {
        this.closePanel();
        const threadId = mail.thread_id || mail.id;
        this._router.navigate(['/mailbox', 'inbox', '1', threadId]);
    }

    /**
     * Navigate to the mailbox inbox
     */
    openMailbox(): void {
        this.closePanel();
        this._router.navigate(['/mailbox']);
    }

    /**
     * Get sender display name
     */
    getSenderName(mail: any): string {
        if (mail.from) {
            return mail.from.name || mail.from.email || 'Unknown';
        }
        return 'Unknown';
    }

    /**
     * Get sender email
     */
    getSenderEmail(mail: any): string {
        if (mail.from) {
            return mail.from.email || '';
        }
        return '';
    }

    /**
     * Track by function for ngFor
     */
    trackByFn(index: number, item: any): string {
        return item.id || index.toString();
    }

    /**
     * Create the overlay for the panel
     */
    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(this._messagesOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                ]),
        });

        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }
}
